/* global axios */
import ApiClient from '../ApiClient';

// Viewer's UTC offset in hours, matching the reports API convention so the
// backend can anchor calendar ranges to the viewer's day.
const getTimezoneOffset = () => -new Date().getTimezoneOffset() / 60;

const buildAssistantFormData = (assistantData, avatarFile = null) => {
  const formData = new FormData();
  const { name, description, config, response_guidelines, guardrails } =
    assistantData;

  if (name !== undefined) formData.append('assistant[name]', name);
  if (description !== undefined) {
    formData.append('assistant[description]', description);
  }

  if (config) {
    Object.entries(config).forEach(([key, value]) => {
      formData.append(`assistant[config][${key}]`, value);
    });
  }

  if (response_guidelines) {
    response_guidelines.forEach(guideline => {
      formData.append('assistant[response_guidelines][]', guideline);
    });
  }

  if (guardrails) {
    guardrails.forEach(guardrail => {
      formData.append('assistant[guardrails][]', guardrail);
    });
  }

  if (avatarFile) {
    formData.append('assistant[avatar]', avatarFile);
  }

  return formData;
};

class CaptainAssistant extends ApiClient {
  constructor() {
    super('captain/assistants', { accountScoped: true });
  }

  get({ page = 1, searchKey } = {}) {
    return axios.get(this.url, {
      params: {
        page,
        searchKey,
      },
    });
  }

  create(data) {
    if (data instanceof FormData) {
      return axios.post(this.url, data);
    }

    return axios.post(this.url, { assistant: data });
  }

  update(id, data) {
    if (data instanceof FormData) {
      return axios.patch(`${this.url}/${id}`, data);
    }

    return axios.patch(`${this.url}/${id}`, { assistant: data });
  }

  deleteAvatar(id) {
    return axios.delete(`${this.url}/${id}/avatar`);
  }

  uploadAvatar(id, avatarFile) {
    const formData = new FormData();
    formData.append('assistant[avatar]', avatarFile);

    return axios.post(`${this.url}/${id}/avatar`, formData);
  }

  buildFormData(assistantData, avatarFile) {
    return buildAssistantFormData(assistantData, avatarFile);
  }

  playground({ assistantId, messageContent, messageHistory }) {
    return axios.post(`${this.url}/${assistantId}/playground`, {
      message_content: messageContent,
      message_history: messageHistory,
    });
  }

  getMetrics({ assistantId, range, signal }) {
    const requestConfig = {
      params: { range, timezone_offset: getTimezoneOffset() },
    };
    if (signal) requestConfig.signal = signal;

    return axios.get(`${this.url}/${assistantId}/metrics`, requestConfig);
  }

  getFaqStats({ assistantId, signal }) {
    const requestConfig = {};
    if (signal) requestConfig.signal = signal;

    return axios.get(`${this.url}/${assistantId}/faq_stats`, requestConfig);
  }

  getSummary({ assistantId, range, stats }) {
    return axios.get(`${this.url}/${assistantId}/summary`, {
      params: { range, timezone_offset: getTimezoneOffset(), stats },
    });
  }

  getDrilldown({ assistantId, metric, range, page, signal }) {
    const requestConfig = {
      params: {
        metric,
        range,
        timezone_offset: getTimezoneOffset(),
        page,
      },
    };
    if (signal) requestConfig.signal = signal;

    return axios.get(`${this.url}/${assistantId}/drilldown`, requestConfig);
  }
}

export default new CaptainAssistant();
